/**
 * HVP Event Bus
 *
 * Lightweight event system for inter-component communication
 * Provides pub/sub pattern without external dependencies
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export class HVPEventBus {
    constructor() {
        this.events = new Map();
        this.onceEvents = new WeakMap();
    }

    /**
     * Subscribe to an event
     *
     * @param {string} event - Event name
     * @param {Function} callback - Event handler
     * @returns {Function} Unsubscribe function
     */
    on(event, callback) {
        if (!this.events.has(event)) {
            this.events.set(event, new Set());
        }

        const handlers = this.events.get(event);
        handlers.add(callback);

        // Return unsubscribe function
        return () => this.off(event, callback);
    }

    /**
     * Subscribe to an event once
     *
     * @param {string} event - Event name
     * @param {Function} callback - Event handler
     * @returns {Function} Unsubscribe function
     */
    once(event, callback) {
        const wrapper = (...args) => {
            callback(...args);
            this.off(event, wrapper);
        };

        // Store reference to original callback for potential manual removal
        if (!this.onceEvents.has(wrapper)) {
            const map = new Map();
            map.set('original', callback);
            this.onceEvents.set(wrapper, map);
        }

        return this.on(event, wrapper);
    }

    /**
     * Unsubscribe from an event
     *
     * @param {string} event - Event name
     * @param {Function} callback - Event handler to remove
     * @returns {boolean} True if handler was removed
     */
    off(event, callback) {
        if (!this.events.has(event)) {
            return false;
        }

        const handlers = this.events.get(event);
        const removed = handlers.delete(callback);

        // Clean up empty event sets
        if (handlers.size === 0) {
            this.events.delete(event);
        }

        return removed;
    }

    /**
     * Emit an event
     *
     * @param {string} event - Event name
     * @param {*} data - Event data
     * @returns {number} Number of handlers called
     */
    emit(event, data = null) {
        if (!this.events.has(event)) {
            return 0;
        }

        const handlers = this.events.get(event);
        let count = 0;

        // Execute handlers in order
        for (const handler of handlers) {
            try {
                handler(data);
                count++;
            } catch (error) {
                console.error(`[HVP EventBus] Error in event handler for "${event}":`, error);
            }
        }

        return count;
    }

    /**
     * Remove all handlers for an event or all events
     *
     * @param {string} [event] - Specific event to clear, or all if not provided
     */
    clear(event = null) {
        if (event === null) {
            this.events.clear();
        } else {
            this.events.delete(event);
        }
    }

    /**
     * Get number of handlers for an event
     *
     * @param {string} event - Event name
     * @returns {number} Number of handlers
     */
    listenerCount(event) {
        return this.events.has(event) ? this.events.get(event).size : 0;
    }

    /**
     * Get all event names
     *
     * @returns {string[]} Array of event names
     */
    eventNames() {
        return Array.from(this.events.keys());
    }

    /**
     * Check if event has handlers
     *
     * @param {string} event - Event name
     * @returns {boolean} True if event has handlers
     */
    hasListeners(event) {
        return this.listenerCount(event) > 0;
    }
}

export default HVPEventBus;
